import type { ResolveMapEntry } from '@ms-cloudpack/common-types';
import { addHashUrl } from '@ms-cloudpack/path-string-parsing';
import type { PartialContext } from '../types/Context.js';
import type { PackageUpdatePayload } from '../types/PackageUpdatePayload.js';

/**
 * Creates a package update payload for HMR (Hot Module Replacement) updates.
 *
 * This function generates updated bundle URLs for all entry points of a package by replacing
 * the old hash with a new HMR hash that includes a timestamp to prevent ESM module caching issues.
 *
 * @param params - Configuration object
 * @param params.input - Package information including entry, name, version, and hash
 * @param params.context - Partial context containing session with import map and package import paths
 * @returns Package update payload with updated entry point URLs, or undefined if no import map exists
 */
export function createPackageUpdatePayload(params: {
  input: { packageEntry: Pick<ResolveMapEntry, 'path' | 'name' | 'version'>; hash: string };
  context: PartialContext<'session', 'importMap' | 'packageImportPaths'>;
}): PackageUpdatePayload | undefined {
  // Extract the package entry metadata (name, version, path) and the new content hash
  const { packageEntry, hash } = params.input;
  const { session } = params.context;

  // The import map is required to update module URLs
  // If it doesn't exist, we can't proceed with creating the update payload
  if (!session.importMap) {
    return undefined;
  }

  // Get all import paths (entry points) for this package
  // Example: ['@mypackage/lib', '@mypackage/utils'] for a package with multiple exports
  const importPaths = session.packageImportPaths.get(packageEntry.path) || [];

  // The imports object maps import paths to their bundle URLs
  // Example: { '@mypackage/lib': '/h-abc123/mypackage-lib.js' }
  const imports = session.importMap.imports;

  // Create a unique HMR hash by appending a timestamp to the content hash
  // This ensures each HMR update gets a unique URL, even if the content hash is the same
  //
  // Why we need the timestamp:
  // - ESM modules are cached by URL in the browser
  // - If code changes from A -> B -> A, the content hash might be the same for the first and third state
  // - Without the timestamp, the browser would serve the cached version instead of re-evaluating
  // - The timestamp ensures every HMR update triggers a fresh module load
  //
  // Example: hash='abc123' becomes hmrHash='abc123-1704067200000'
  const hmrHash = `${hash}-${Date.now()}`;

  // Transform each import path into an entry point with an updated bundle URL
  const entryPoints = importPaths
    .map((importPath) => {
      // Look up the current bundle URL for this import path
      // Example: importPath='mypackage/lib' -> oldBundleUrl='/h-old123/mypackage-lib.js'
      const oldBundleUrl = imports[importPath];

      // If there's no existing URL mapping, skip this import path
      // This can happen if the package was recently added or hasn't been bundled yet
      if (!oldBundleUrl) {
        return null;
      }

      // Replace the hash segment in the URL with the new HMR hash
      // Uses addHashUrl to safely replace the hash by finding the package@version location
      // This avoids collision risks with other 'h-' segments that might appear in paths
      //
      // Example transformation:
      // Old URL: 'http://localhost:8080/mypackage@1.0.0/h-old123/bundled/lib.js'
      // New URL: 'http://localhost:8080/mypackage@1.0.0/h-abc123-1704067200000/bundled/lib.js'
      const newBundleUrl = addHashUrl({
        url: oldBundleUrl,
        packageName: packageEntry.name,
        version: packageEntry.version,
        hash: hmrHash,
      });

      // Return an entry point object with the import path and its new bundle URL
      return { importPath, bundleUrl: newBundleUrl };
    })
    // Remove any null entries (import paths that didn't have existing URLs)
    .filter((entry) => entry !== null);

  // Return the complete payload with package metadata and updated entry points
  // This payload will be sent to clients to notify them of the package update
  return {
    packageName: packageEntry.name,
    version: packageEntry.version,
    entryPoints,
  };
}
